import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import useScrollAnimation from '../hooks/useScrollAnimation';
import { fadeInUp, fadeInLeft, fadeInRight, staggerContainer, scaleUp } from '../utils/animationVariants';
import CTA from '../components/Cta';
import FAQ from '../components/Faq';
import toast from 'react-hot-toast';
import { useSEO } from '../hooks/useSEO';

const NFCCardsPage = () => {
  useSEO({
    title: "NFC Business Cards - Smart Contactless Sharing | AM CARDMATE",
    description: "Premium NFC business cards for instant contact sharing. Professional, sustainable, and feature-rich digital cards that work with any smartphone.",
   image: "/assets/nfccards.jpg",
    keywords: "NFC business cards, smart cards, contactless cards, digital business cards, NFC technology, tap to share"
  });

  useEffect(() => {
  const schemas = [
    {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "AM CARDMATE NFC Business Cards",
      "description": "Premium NFC-enabled business cards for instant contact sharing",
      "brand": {
        "@type": "Brand",
        "name": "AM CARDMATE"
      },
      "offers": {
        "@type": "AggregateOffer",
        "priceCurrency": "GBP",
        "lowPrice": "9.99",
        "highPrice": "19.99",
        "offerCount": "3",
        "offers": [
          {
            "@type": "Offer",
            "name": "Executive Black NFC Card",
            "price": "9.99",
            "priceCurrency": "GBP",
            "availability": "https://schema.org/InStock",
            "deliveryLeadTime": {
              "@type": "QuantitativeValue",
              "minValue": 3,
              "maxValue": 5,
              "unitCode": "DAY"
            }
          },
          {
            "@type": "Offer",
            "name": "Vibrant Gradient NFC Card",
            "price": "12.99",
            "priceCurrency": "GBP",
            "availability": "https://schema.org/InStock"
          },
          {
            "@type": "Offer",
            "name": "Eco Wooden NFC Card",
            "price": "19.99",
            "priceCurrency": "GBP",
            "availability": "https://schema.org/InStock"
          }
        ]
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "NFC Business Card Collection",
      "description": "Premium collection of NFC-enabled business cards",
      "numberOfItems": 3,
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Executive Black",
          "description": "Sleek matte black finish with premium feel"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Vibrant Gradient",
          "description": "Eye-catching gradient design"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": "Eco Wooden",
          "description": "Sustainable bamboo construction"
        }
      ]
    }
  ];
  
  schemas.forEach(schema => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
  });
}, []);
  const { ref, controls } = useScrollAnimation(0.1);
  const [selectedCard, setSelectedCard] = useState('wooden');
  const [activeTab, setActiveTab] = useState('features');
  const [quantities, setQuantities] = useState({
    wooden: 1,
    blackPlastic: 1,
    appStyle: 1
  });
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [showDemoPopup, setShowDemoPopup] = useState(false);
  
  const { addToCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  
  const scrollToCards = () => {
    const cardsSection = document.getElementById('nfc-cards-section');
    if (cardsSection) {
      cardsSection.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  
  const nfcCards = {
    wooden: {
      id: 'wooden',
      name: 'Eco Wooden Card',
      tagline: 'Sustainable Elegance',
      description: 'Crafted from premium bamboo with unique grain patterns. Perfect for eco-conscious professionals who value sustainability.',
      longDescription: 'Our Eco Wooden Card represents the future of sustainable business networking. Each card is crafted from rapidly renewable bamboo, laser-engraved with your details, and embedded with advanced NFC technology. The natural grain patterns make every card unique, while the eco-friendly construction aligns with modern corporate responsibility values.',
      price: 19.99,
      originalPrice: 29.99,
      image: '/assets/cardwooden.png',
      gradient: 'from-amber-500 to-amber-700',
      type: 'nfc-card',
      features: [
        { icon: 'leaf', title: '100% Sustainable Bamboo', description: 'Made from renewable bamboo resources' },
        { icon: 'fingerprint', title: 'Unique Natural Grain', description: 'Natural patterns make each card one-of-a-kind' },
        { icon: 'award', title: 'Premium Laser Engraving', description: 'Permanent, precise engraving that never fades' },
        { icon: 'shield-alt', title: '5-Year Durability', description: 'Built to last with superior construction' }
      ],
      specifications: {
        'Material': 'Premium Bamboo',
        'Thickness': '1.5mm',
        'Weight': '8g',
        'NFC Chip': 'NTAG216',
        'Read Range': '0-10cm',
        'Data Storage': '888 bytes',
        'Compatibility': 'All NFC Smartphones',
        'Operating Temp': '-25°C to 70°C'
      },
      testimonial: {
        name: 'Michael Green',
        role: 'Sustainability Officer',
        text: 'Perfect alignment with our company values. Clients love the eco-friendly approach and unique texture.',
        rating: 5
      }
    },
    blackPlastic: {
      id: 'blackPlastic',
      name: 'Executive Black',
      tagline: 'Professional Excellence',
      description: 'Sleek matte black finish with premium feel. The ultimate choice for executives and corporate professionals.',
      longDescription: 'Our Executive Black Card combines cutting-edge NFC technology with sophisticated design. The matte black finish exudes professionalism while the durable construction ensures your card maintains its premium appearance. Perfect for executives who want to make a lasting impression.',
      price: 9.99,
      originalPrice: 19.99,
      image: '/assets/cardblack.png',
      gradient: 'from-gray-700 to-gray-900',
      iconGradient: 'from-blue-400 to-blue-600',
      type: 'nfc-card',
      features: [
        { icon: 'water', title: 'Waterproof Design', description: 'IP68 rated for complete water resistance' },
        { icon: 'gem', title: 'Premium Matte Finish', description: 'Sophisticated look that resists fingerprints' },
        { icon: 'bolt', title: 'Instant Recognition', description: 'Professional design that commands attention' },
        { icon: 'check-circle', title: 'Scratch Resistant', description: 'Durable coating protects against daily wear' }
      ],
      specifications: {
        'Material': 'Premium PVC',
        'Thickness': '0.76mm',
        'Weight': '6g',
        'NFC Chip': 'NTAG216',
        'Security': 'Password Protection',
        'Data Retention': '10,000 write cycles',
        'Print Quality': '300 DPI',
        'Water Resistance': 'IP68 Certified'
      },
      testimonial: {
        name: 'Sarah Williams',
        role: 'CEO, Tech Innovations',
        text: 'The Executive Black card perfectly matches our corporate image. It\'s professional and innovative.',
        rating: 5
      }
    },
    appStyle: {
      id: 'appStyle',
      name: 'Vibrant Gradient',
      tagline: 'Modern Innovation',
      description: 'Eye-catching gradient design that matches our app. Stand out at every networking event with style.',
      longDescription: 'Our Vibrant Gradient Card is designed for the modern professional who wants to make a bold statement. The stunning gradient design matches our app interface, creating a cohesive brand experience. The high-gloss finish and vibrant colors ensure you\'ll never be forgotten.',
      price: 12.99,
      originalPrice: 22.99,
      image: '/assets/cardapp.png',
      gradient: 'from-blue-500 to-purple-600',
      type: 'nfc-card',
      features: [
        { icon: 'palette', title: 'Stunning Gradient Design', description: 'Eye-catching colors that match our app' },
        { icon: 'paint-brush', title: 'High-Gloss Finish', description: 'Mirror-like surface with vibrant colors' },
        { icon: 'mobile-alt', title: 'App-Matching Colors', description: 'Seamless brand experience' },
        { icon: 'star', title: 'Conversation Starter', description: 'Unique design that gets people talking' }
      ],
      specifications: {
        'Material': 'Premium PVC',
        'Thickness': '0.76mm',
        'Weight': '6g',
        'NFC Chip': 'NTAG216',
        'UV Protection': 'Yes',
        'Color Process': '4-Color CMYK',
        'Finish Options': 'High Gloss',
        'Encoding Type': 'URL/vCard/Text'
      },
      testimonial: {
        name: 'Alex Chen',
        role: 'Creative Director',
        text: 'The gradient design is absolutely stunning. It\'s a perfect conversation starter at every event.',
        rating: 5
      }
    }
  };
  
  const currentCard = nfcCards[selectedCard];

  const handleQuantityChange = (cardId, newQuantity) => {
    if (newQuantity >= 1) {
      setQuantities({
        ...quantities,
        [cardId]: newQuantity
      });
    }
  };

  const handleAddToCart = async (cardId) => {
    if (!user) {
      toast.error('Please login to add items to cart');
      navigate('/login');
      return;
    }

    const card = nfcCards[cardId];
    const quantity = quantities[cardId];
    
    const cartItem = {
      id: cardId,
      name: card.name,
      description: card.tagline,
      price: card.price,
      quantity: quantity,
      image: card.image,
      type: card.type
    };

    try {
      await addToCart(cartItem);
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Failed to add item to cart');
    }
  };

  const handleBuyNow = async (cardId) => {
    await handleAddToCart(cardId);
    navigate('/cart');
  };
  
  return (
    <>
      <section className="pt-20 md:pt-24 lg:pt-32 pb-16 md:pb-24 bg-gradient-to-b from-white via-light to-white relative overflow-hidden">
        <div className="absolute top-10 right-10 w-[700px] h-[700px] bg-gradient-to-br from-blue-200/20 via-purple-200/20 to-transparent rounded-full filter blur-3xl" />
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div 
            ref={ref}
            className="text-center max-w-5xl mx-auto"
          >
            <div className="badge badge-blue inline-flex mb-6">
              <FontAwesomeIcon icon="wifi" />
              <span>NFC Technology</span>
            </div>
            
            <h1 className="text-h1 mb-6">
              Premium <span className="text-gradient-blue-pink">NFC Business Cards</span>
            </h1>
            
            <p className="text-body-lg text-gray max-w-3xl mx-auto mb-10">
              Experience the future of networking with our premium NFC-enabled business cards. 
              Share your digital profile instantly with just a tap.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <button 
                className="btn btn-primary group w-full sm:w-auto"
                onClick={() => setShowDemoPopup(true)}
              >
                <FontAwesomeIcon icon="play-circle" />
                <span>Watch How It Works</span>
                <FontAwesomeIcon 
                  icon="arrow-right" 
                  className="ml-1 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" 
                />
              </button>
              
              <Link 
                to="/about" 
                className="btn btn-outline w-full sm:w-auto"
              >
                <FontAwesomeIcon icon="info-circle" />
                <span>Learn More</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section id="nfc-cards-section" className="py-12 sm:py-16 md:py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-h2 text-dark mb-4">Choose Your Style</h2>
            <p className="text-body text-gray max-w-2xl mx-auto mb-8">
              Select from our premium collection of NFC-enabled business cards. Each designed to match your professional style.
            </p>
            
            <div className="inline-flex bg-gray-100 p-1.5 sm:p-2 rounded-2xl shadow-inner">
              <button
                className={`px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold transition-all duration-300 text-body-sm ${
                  selectedCard === 'wooden'
                    ? 'bg-white text-primary-blue shadow-md'
                    : 'text-gray hover:text-dark'
                }`}
                onClick={() => setSelectedCard('wooden')}
              >
                Wooden
              </button>
              
              <button
                className={`px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold transition-all duration-300 text-body-sm ${
                  selectedCard === 'blackPlastic'
                    ? 'bg-white text-primary-blue shadow-md'
                    : 'text-gray hover:text-dark'
                }`}
                onClick={() => setSelectedCard('blackPlastic')}
              >
                Black
              </button>
              
              <button
                className={`px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold transition-all duration-300 text-body-sm ${
                  selectedCard === 'appStyle'
                    ? 'bg-white text-primary-blue shadow-md'
                    : 'text-gray hover:text-dark'
                }`}
                onClick={() => setSelectedCard('appStyle')}
              >
                Gradient
              </button>
            </div>
          </div>
          
          <div 
            key={selectedCard}
            className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start lg:items-center max-w-7xl mx-auto"
          >
            <div className="relative order-1 lg:order-1">
              <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 shadow-xl sm:shadow-2xl">
                {currentCard.price < currentCard.originalPrice && (
                  <div className="absolute top-2 left-2 sm:top-4 sm:left-4 bg-red-500 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-body-sm font-bold shadow-lg z-10">
                    SAVE {Math.round((1 - currentCard.price / currentCard.originalPrice) * 100)}%
                  </div>
                )}
                
                <div className="relative h-[250px] sm:h-[300px] md:h-[400px] flex items-center justify-center">
                  <img 
                    src={currentCard.image}
                    alt={currentCard.name}
                    className="relative z-10 w-full h-full object-contain drop-shadow-xl sm:drop-shadow-2xl"
                  />
                </div>
              </div>
            </div>
            
            <div className="order-2 lg:order-2">
              <div className={`inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full mb-4 bg-gradient-to-r ${currentCard.gradient} text-white text-body-sm font-semibold`}>
                <FontAwesomeIcon icon="award" />
                <span>{currentCard.tagline}</span>
              </div>
              
              <h2 className="text-h2 text-dark mb-4">
                {currentCard.name}
              </h2>
              
              <p className="text-body text-gray mb-6 leading-relaxed">
                {currentCard.longDescription}
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-8">
                {currentCard.features.map((feature, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-3"
                  >
                    <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center text-white flex-shrink-0 bg-gradient-to-br ${
                      currentCard.iconGradient || currentCard.gradient
                    }`}>
                      <FontAwesomeIcon icon={feature.icon} className="text-xs sm:text-sm" />
                    </div>
                    <div>
                      <h4 className="text-h5 text-dark">{feature.title}</h4>
                      <p className="text-body-sm text-gray hidden sm:block">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl sm:rounded-2xl p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
                  <div>
                    <span className="text-h2 text-dark">£{currentCard.price}</span>
                    {currentCard.originalPrice > currentCard.price && (
                      <span className="text-h4 text-gray line-through ml-3">£{currentCard.originalPrice}</span>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2 sm:gap-3 bg-white rounded-lg p-1">
                    <button 
                      className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray hover:bg-gray-200 transition-colors"
                      onClick={() => handleQuantityChange(selectedCard, quantities[selectedCard] - 1)}
                    >
                      <FontAwesomeIcon icon="minus" />
                    </button>
                    <span className="w-10 sm:w-12 text-center text-body font-semibold">{quantities[selectedCard]}</span>
                    <button 
                      className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray hover:bg-gray-200 transition-colors"
                      onClick={() => handleQuantityChange(selectedCard, quantities[selectedCard] + 1)}
                    >
                      <FontAwesomeIcon icon="plus" />
                    </button>
                  </div>
                </div>
                
                <div className="flex gap-3 sm:gap-4 mb-4">
                  <button 
                    onClick={() => handleAddToCart(selectedCard)}
                    className="btn btn-primary flex-1"
                  >
                    <FontAwesomeIcon icon="shopping-cart" />
                    <span>Add to Cart</span>
                  </button>
                  
                  <button 
                    onClick={() => handleBuyNow(selectedCard)}
                    className="btn btn-outline px-3 sm:px-4"
                  >
                    <FontAwesomeIcon icon="bolt" />
                  </button>
                </div>
                
                <div className="flex items-center justify-center gap-4 sm:gap-6 text-body-sm text-gray">
                  <div className="flex items-center gap-1">
                    <FontAwesomeIcon icon="truck" className="text-primary-blue" />
                    <span>Free Shipping</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <FontAwesomeIcon icon="shield-alt" className="text-green-500" />
                    <span>Secure Payment</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-12 sm:mt-16 max-w-5xl mx-auto">
            <div className="flex justify-start sm:justify-center mb-6 sm:mb-8 border-b border-gray-200 overflow-x-auto">
              {['features', 'specifications', 'reviews'].map((tab) => (
                <button
                  key={tab}
                  className={`px-4 sm:px-6 py-2.5 sm:py-3 font-semibold capitalize transition-all relative whitespace-nowrap text-body ${
                    activeTab === tab ? 'text-primary-blue' : 'text-gray hover:text-dark'
                  }`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab}
                  {activeTab === tab && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-blue" />
                  )}
                </button>
              ))}
            </div>
            
            {activeTab === 'features' && (
              <div
                key="features"
                className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6"
              >
                {currentCard.features.map((feature, index) => (
                  <div 
                    key={index}
                    className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all"
                  >
                    <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center text-white mb-4 bg-gradient-to-br ${
                      currentCard.iconGradient || currentCard.gradient
                    }`}>
                      <FontAwesomeIcon icon={feature.icon} className="text-lg sm:text-xl" />
                    </div>
                    <h3 className="text-h4 text-dark mb-2">{feature.title}</h3>
                    <p className="text-body text-gray">{feature.description}</p>
                  </div>
                ))}
              </div>
            )}
            
            {activeTab === 'specifications' && (
              <div
                key="specifications"
                className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-8 shadow-lg"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  {Object.entries(currentCard.specifications).map(([key, value], index) => (
                    <div 
                      key={key}
                      className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-2 sm:py-3 border-b border-gray-100"
                    >
                      <span className="text-body text-gray font-medium">{key}</span>
                      <span className="text-body text-dark font-semibold">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {activeTab === 'reviews' && (
              <div
                key="reviews"
                className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-8 shadow-lg"
              >
                <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-primary-blue to-accent-purple flex items-center justify-center text-white font-bold text-h4">
                    {currentCard.testimonial.name.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-1 sm:gap-2 mb-2">
                      {[...Array(currentCard.testimonial.rating)].map((_, i) => (
                        <FontAwesomeIcon key={i} icon="star" className="text-yellow-500" />
                      ))}
                    </div>
                    <p className="text-body text-gray mb-4 italic">"{currentCard.testimonial.text}"</p>
                    <div>
                      <div className="text-h5 text-dark">{currentCard.testimonial.name}</div>
                      <div className="text-body-sm text-gray">{currentCard.testimonial.role}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 md:py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center max-w-6xl mx-auto">
            <div className="relative order-2 lg:order-1">
              <div className="relative">
                <img 
                  src="/assets/image21.png"
                  alt="NFC Technology in Action" 
                  className="w-full rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl"
                />
                <div className="absolute -bottom-4 -right-4 sm:-bottom-6 sm:-right-6 bg-gradient-to-r from-primary-blue to-accent-purple text-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-xl">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <FontAwesomeIcon icon="wifi" className="text-xl sm:text-2xl" />
                    <div>
                      <p className="text-h5">Instant Share</p>
                      <p className="text-body-sm opacity-90">Just tap & connect</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="order-1 lg:order-2">
              <div className="badge badge-pink inline-flex mb-4">
                <FontAwesomeIcon icon="magic" />
                <span>How It Works</span>
              </div>
              
              <h2 className="text-h1 mb-6">
                Sharing Made <span className="text-gradient-blue-pink">Simple</span>
              </h2>
              
              <p className="text-body-lg text-gray mb-8 leading-relaxed">
                Our NFC cards use the same technology as contactless payments. Just tap your card on any smartphone to instantly share your digital profile.
              </p>
              
              <div className="space-y-4 sm:space-y-6">
                {[
                  {
                    step: '1',
                    title: 'Tap or Scan',
                    description: 'Simply tap your NFC card on any smartphone or scan the QR code'
                  },
                  {
                    step: '2',
                    title: 'View Profile',
                    description: 'Your complete digital profile opens instantly in their browser'
                  },
                  {
                    step: '3',
                    title: 'Save & Connect',
                    description: 'They can save your contact, connect on social media, and more'
                  }
                ].map((step, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-3 sm:gap-4"
                  >
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-primary-blue to-accent-purple rounded-lg sm:rounded-xl flex items-center justify-center text-white font-bold flex-shrink-0 text-body">
                      {step.step}
                    </div>
                    <div>
                      <h3 className="text-h4 text-dark mb-1">{step.title}</h3>
                      <p className="text-body text-gray">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12">
            <div className="badge badge-green inline-flex mb-4">
              <FontAwesomeIcon icon="check-circle" />
              <span>Why Choose NFC</span>
            </div>
            
            <h2 className="text-h1 mb-6">
              Transform Your <span className="text-gradient-blue-pink">Networking Experience</span>
            </h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 max-w-6xl mx-auto">
            {[
              {
                icon: 'bolt',
                title: 'Instant Sharing',
                description: 'Share your complete digital profile with a single tap. No app required for recipients.',
                gradient: 'from-yellow-400 to-orange-500'
              },
              {
                icon: 'infinity',
                title: 'Unlimited Updates',
                description: 'Change your information anytime without reprinting. Your card stays current forever.',
                gradient: 'from-blue-400 to-purple-500'
              },
              {
                icon: 'chart-line',
                title: 'Track Engagement',
                description: 'Track views, contact saves, QR code scans, shares, device types, and daily activity patterns.',
                gradient: 'from-green-400 to-teal-500'
              },
              {
                icon: 'leaf',
                title: 'Eco-Friendly',
                description: 'Reduce paper waste with one reusable card that replaces thousands of paper cards.',
                gradient: 'from-green-500 to-green-700'
              },
              {
                icon: 'shield-alt',
                title: 'Secure & Private',
                description: 'You decide what to show and what to hide.',
                gradient: 'from-purple-400 to-pink-500'
              },
              {
                icon: 'globe',
                title: 'Works Everywhere',
                description: 'Compatible with all modern smartphones. No special app needed to receive info.',
                gradient: 'from-blue-500 to-cyan-500'
              }
            ].map((benefit, index) => (
              <div 
                key={index}
                className="relative group"
              >
                <div className="relative bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-br ${benefit.gradient} flex items-center justify-center text-white mb-4 shadow-lg`}>
                    <FontAwesomeIcon icon={benefit.icon} className="text-xl sm:text-2xl" />
                  </div>
                  <h3 className="text-h4 text-dark mb-2">{benefit.title}</h3>
                  <p className="text-body text-gray">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 md:py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12">
            <div className="badge badge-blue inline-flex mb-4">
              <FontAwesomeIcon icon="balance-scale" />
              <span>Compare Cards</span>
            </div>
            
            <h2 className="text-h1 mb-6">
              Find Your Perfect <span className="text-gradient-blue-pink">NFC Card</span>
            </h2>
          </div>
       
          <div className="block sm:hidden">
            <div className="space-y-4">
              {Object.values(nfcCards).map((card) => (
                <div key={card.id} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                  <div className="bg-gradient-to-r from-primary-blue to-accent-purple text-white p-4">
                    <h3 className="text-h5">{card.name.split(' ')[0]}</h3>
                    <p className="text-body-sm opacity-90">{card.tagline}</p>
                  </div>
                  <div className="p-4 space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-body-sm text-gray font-medium">Price</span>
                      <span className="text-body text-dark font-semibold">£{card.price}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-body-sm text-gray font-medium">Material</span>
                      <span className="text-body-sm text-dark font-semibold">{card.specifications.Material}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-body-sm text-gray font-medium">NFC Chip</span>
                      <span className="text-body-sm text-dark font-semibold">{card.specifications['NFC Chip']}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-body-sm text-gray font-medium">Water Resistance</span>
                      <span className="text-body-sm text-dark font-semibold">
                        {card.id === 'blackPlastic' ? 'IP68 Certified' : card.id === 'wooden' ? 'Natural Coating' : 'UV Protected'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-body-sm text-gray font-medium">Special Feature</span>
                      <span className="text-body-sm text-dark font-semibold">
                        {card.id === 'blackPlastic' ? 'Matte Finish' : card.id === 'wooden' ? 'Unique Grain' : 'Gradient Design'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="hidden sm:block max-w-6xl mx-auto">
            <div className="overflow-x-auto rounded-2xl shadow-lg">
              <table className="w-full bg-white">
                <thead>
                  <tr className="bg-gradient-to-r from-primary-blue to-accent-purple text-white">
                    <th className="p-4 text-left text-h5">Feature</th>
                    <th className="p-4 text-center">
                      <div className="text-h5">Wooden</div>
                      <div className="text-body-sm opacity-80">Eco Card</div>
                    </th>
                    <th className="p-4 text-center">
                      <div className="text-h5">Black</div>
                      <div className="text-body-sm opacity-80">Executive</div>
                    </th>
                    <th className="p-4 text-center">
                      <div className="text-h5">Gradient</div>
                      <div className="text-body-sm opacity-80">Vibrant</div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { feature: 'Price', key: 'price', format: (val) => `£${val}` },
                    { feature: 'Material', key: 'material' },
                    { feature: 'NFC Chip', key: 'chip' },
                    { feature: 'Water Resistance', key: 'water' },
                    { feature: 'Special Feature', key: 'special' }
                  ].map((row, index) => (
                    <tr 
                      key={row.feature}
                      className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}
                    >
                      <td className="p-4 text-body text-dark font-semibold">{row.feature}</td>
                      {Object.values(nfcCards).map((card) => {
                        let value;
                        switch(row.key) {
                          case 'price':
                            value = card.price;
                            break;
                          case 'material':
                            value = card.specifications.Material;
                            break;
                          case 'chip':
                            value = card.specifications['NFC Chip'];
                            break;
                          case 'water':
                            value = card.id === 'blackPlastic' ? 'IP68 Certified' : card.id === 'wooden' ? 'Natural Coating' : 'UV Protected';
                            break;
                          case 'special':
                            value = card.id === 'blackPlastic' ? 'Matte Finish' : card.id === 'wooden' ? 'Unique Grain' : 'Gradient Design';
                            break;
                          default:
                            value = '-';
                        }
                        return (
                          <td key={card.id} className="p-4 text-center text-body">
                            {row.format ? row.format(value) : value}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16 md:py-24 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center max-w-6xl mx-auto">
            <div className="order-2 lg:order-1">
              <div className="badge badge-blue inline-flex mb-6">
                <FontAwesomeIcon icon="bolt" />
                <span>Limited Time Offer</span>
              </div>
              
              <h2 className="text-h1 mb-6">
                Ready to Upgrade Your Networking?
              </h2>
              
              <p className="text-body-lg text-gray mb-8">
                Join thousands of professionals who've transformed their networking with NFC business cards. 
                Get 25% off your first order today!
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8">
                <button 
                  onClick={scrollToCards}
                  className="btn btn-primary btn-lg w-full sm:w-auto"
                >
                  <FontAwesomeIcon icon="shopping-cart" />
                  <span>Shop NFC Cards</span>
                </button>
                
                <button 
                  className="btn btn-outline btn-lg w-full sm:w-auto"
                  onClick={() => setShowDemoPopup(true)}
                >
                  <FontAwesomeIcon icon="play-circle" />
                  <span>Watch Demo</span>
                </button>
              </div>
              
              <div className="flex flex-wrap gap-4 sm:gap-6 text-body-sm">
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon="truck" className="text-accent-yellow" />
                  <span>Fast Shipping Worldwide</span>
                </div>
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon="shield-alt" className="text-accent-green" />
                  <span>Secure Platform</span>
                </div>
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon="headset" className="text-primary-pink" />
                  <span>24/7 Support</span>
                </div>
              </div>
            </div>
          
            <div className="relative order-1 lg:order-2">
              <img 
                src="/assets/image26.png" 
                alt="NFC Cards Collection" 
                className="w-full rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl"
              />
              <div className="absolute -top-4 -right-4 sm:-top-6 sm:-right-6 bg-accent-yellow text-dark rounded-xl sm:rounded-2xl px-4 sm:px-6 py-2 sm:py-3 shadow-xl text-h5">
                <FontAwesomeIcon icon="percentage" className="mr-2" />
                25% OFF
              </div>
            </div>
          </div>
        </div>
      </section>

      <FAQ />

      {showDemoPopup && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowDemoPopup(false)}
        >
          <div 
            className="bg-white rounded-2xl p-6 sm:p-8 max-w-md w-full text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-primary-blue to-accent-purple rounded-full flex items-center justify-center mx-auto mb-6">
              <FontAwesomeIcon icon="play-circle" className="text-white text-2xl sm:text-3xl" />
            </div>
            
            <h3 className="text-h3 text-dark mb-4">Demo Videos Coming Soon!</h3>
            <p className="text-body text-gray mb-6">
              We're working on creating amazing demo videos to show you exactly how our NFC cards work. 
              Bear with us - they'll be ready soon!
            </p>
            
            <button 
              className="btn btn-primary w-full"
              onClick={() => setShowDemoPopup(false)}
            >
              Got it!
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default NFCCardsPage;